
export type Locale = 'en' | 'ru';

export interface Translations {
  [key: string]: string | Translations;
}

// Кэш переводов
let translationsCache: { [locale: string]: Translations } = {};

// Определение языка браузера
const getBrowserLocale = (): Locale => {
  if (typeof navigator === 'undefined') {
    return 'en';
  }
  
  const browserLang = navigator.language || navigator.languages?.[0] || 'en';
  // Если язык браузера начинается с 'ru', возвращаем 'ru', иначе 'en'
  return browserLang.toLowerCase().startsWith('ru') ? 'ru' : 'en';
};

// Текущая локаль (инициализируется языком браузера или из localStorage)
let currentLocale: Locale = (() => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('locale') as Locale;
    if (saved === 'ru' || saved === 'en') {
      return saved;
    }
    return getBrowserLocale();
  }
  return 'en';
})();

// Загружаем переводы с сервера или используем локальные
const loadTranslations = async (locale: Locale): Promise<Translations> => {
  // Если уже загружены, возвращаем из кэша
  if (translationsCache[locale]) {
    return translationsCache[locale];
  }

  // Пока используем только локальные переводы
  // В будущем можно добавить загрузку с сервера
  // try {
  //   const response = await fetch(`${API_BASE_URL}/api/translations/${locale}`, {
  //     credentials: 'include',
  //   });
  //   if (response.ok) {
  //     const data = await response.json();
  //     translationsCache[locale] = data;
  //     return data;
  //   }
  // } catch (error) {
  //   console.warn('Could not load translations from server, using local translations');
  // }

  // Используем локальные переводы
  return getLocalTranslations(locale);
};

// Локальные переводы (из Laravel lang файлов)
const getLocalTranslations = (locale: Locale): Translations => {
  if (locale === 'ru') {
    return {
      promocodeGenerator: 'Promocode generator',
      adminPanel: 'Панель администратора',
      categories: 'Категории',
      templates: 'Шаблоны',
      users: 'Пользователи',
      user: { notFound: 'Пользователей нет' },
      access: { hasAccess: 'Есть доступ', noAccess: 'Нет доступа' },
      home: 'Главная',
      telegramAccess: 'Доступ к Telegram',
      giveTelegramAccess: 'Предоставить доступ к боту',
      logout: 'Выйти',
      theme: { light: 'Светлая тема', dark: 'Тёмная тема' },
      chooseBot: 'Выберите бота',
      createdBy: 'Создал: ',
      permissions: 'Разрешения',
      category: {
        create: 'Создать категорию (Страна/язык)',
        enter: 'Введите категорию',
        disable: 'Отключить',
        enable: 'Включить',
        isDisabled: 'Категория отключена',
        isEnabled: 'Категория включена',
        promocodeTemplates: 'Показать шаблоны промокодов',
        notFound: 'Категорий нет',
      },
      promocode: {
        create: 'Создать шаблон промокода',
        edite: 'Редактировать шаблон промокода',
        image: 'Изображение',
        video: 'Видео',
        waitingForSetup: 'В ожидании настройки',
        notFound: 'Шаблонов нет',
        restartVideo: 'Перезапустить видео',
        restartGif: 'Перезапустить GIF',
        mute: 'Выключить звук',
        unmute: 'Включить звук',
      },
      manage: {
        edit: 'Изменить',
        delete: 'Удалить',
        save: 'Сохранить',
        saving: 'Сохранение…',
        attach: 'Прикрепить',
        search: 'Искать',
        create: 'Создать',
        back: 'Назад',
      },
      paginationOf: 'из',
      errors: {
        loadTemplatesFailed: 'Не удалось загрузить шаблоны',
        deleteTemplate: 'Ошибка удаления шаблона',
        createTemplate: 'Ошибка создания шаблона',
        updateTemplate: 'Ошибка обновления шаблона',
        deleteTelegramUser: 'Ошибка удаления пользователя Telegram',
        toggleAccess: 'Ошибка изменения доступа',
        deleteUser: 'Ошибка удаления пользователя',
        createTelegramUser: 'Ошибка создания пользователя Telegram',
        updateTelegramUser: 'Ошибка обновления пользователя Telegram',
        invalidPassword: 'Некорректный пароль',
        createUser: 'Ошибка создания пользователя',
        updateUser: 'Ошибка обновления пользователя',
        deleteCategory: 'Ошибка удаления категории',
        createCategory: 'Ошибка создания категории',
        updateCategory: 'Ошибка обновления категории',
        toggleCategoryVisibility: 'Ошибка изменения видимости категории',
        loginFailed: 'Ошибка входа',
        loadCategoriesFailed: 'Не удалось загрузить категории',
        passwordsDoNotMatch: 'Пароли не совпадают',
        registrationFailed: 'Ошибка регистрации',
      },
      validation: {
        password: {
          minLength: 'Минимум 8 символов',
          uppercase: 'Нужна хотя бы одна большая буква',
          digit: 'Нужна хотя бы одна цифра',
          special: 'Нужен хотя бы один спецсимвол (!@#$%&*+-=?)',
        },
        selectFile: 'Выберите файл',
        selectCategory: 'Выберите хотя бы одну категорию',
        selectArea: 'Выделите область промокода на изображении',
        selectFont: 'Выберите шрифт',
      },
      formField: {
        name: 'Имя',
        image: 'Изображение',
        file: 'Файл',
        files: 'файлов',
        categories: 'Категории',
        parent: 'Родительская категория',
        parameters: 'Параметры',
        promoTest: 'Тест промо',
        testBtn: 'Тестировать',
        imageParams: 'Параметры изображения',
        textColor: 'Цвет текста',
        textFontSize: 'Размер шрифта текста',
        textFont: 'Шрифт текста',
        substrateSettings: 'Настройки подложки',
        render: 'Рендеринг',
        bgColor: 'Цвет фона',
        upperRadius: 'Верхний радиус',
        sidesRadius: 'Радиус сторон',
        borderSize: 'Размер границы',
        borderColor: 'Цвет границы',
        shadowSettings: 'Настройки тени',
        shadowBold: 'Наследование тени',
        shadowUpperSize: 'Размер верхней тени',
        shadowSidesSize: 'Размер теней по бокам',
        headerSettings: 'Настройки заголовка',
        renderHeader: 'Рендеринг заголовка',
        headerColor: 'Цвет заголовка',
        headerText: 'Текст заголовка',
        headerTextFont: 'Шрифт текста заголовка',
        headerTextFontSize: 'Размер шрифта текста заголовка',
        telegramText: 'Текст в Telegram',
        password: 'Пароль',
        passwordLeaveEmpty: 'Оставьте пустым, чтобы не менять',
        generate: 'Сгенерировать',
        generatePassword: 'Сгенерировать',
        passwordRules: 'От 8 символов, обязательны: спецсимвол, цифра, большая буква',
        permissions: 'Права',
        namePlaceholder: 'Введите имя',
        passwordPlaceholder: 'Введите пароль',
        chooseUser: 'Выберите пользователя',
        telegramBot: 'Telegram бот',
        telegramUserName: 'Имя пользователя в Telegram',
        telegramUserId: 'ID пользователя в Telegram',
        selectBot: 'Выберите бота',
        telegramUserPlaceholder: 'Введите имя пользователя в Telegram',
        telegramUserIdPlaceholder: 'Введите ID пользователя в Telegram',
        chooseParent: 'Выберите родительскую категорию',
        roleId: 'ID роли',
        promocodeTemplates: 'Шаблоны промокодов',
        angle: 'Угол',
        startTime: 'Начало',
        endTime: 'Конец',
        createUser: 'Создать нового пользователя',
        giveAdminAccess: 'Дать доступ пользователю телеграм',
        yourCurrentTemplate: 'Ваш текущий шаблон',
        dragndrop:'Перетащите файл или нажмите для выбора'
      },
      attachUsers: 'Дать пользователю права',
      selectUser: 'Выберите пользователя',
      chooseUser: 'Выберите пользователя',
      userName: 'Имя',
      userEmail: 'Почта',
      userPermissions: 'Разрешения',
      createdAt: 'Создан',
      categoryName: 'Категория',
      categoryCreateBtn: 'Создать категорию',
      Login: 'Вход',
      'Logging in...': 'Вход...',
      'Loading...': 'Загрузка...',
      helpers: {
        category: {
          title: "Также, это кнопки в телеграм боте.",
          name: 'Название категории',
          promocodeTemplate: 'Вы можете выбрать другой шаблон промокода или шаблоны для категории',
          parentCategory: "Если вы хотите создать подкатегорию, вам нужно выбрать основную категорию. Например, если вы хотите создать новую категорию под названием 'Видео' и выбрать 'ENG' в качестве родительской категории, после сохранения вы получите 'ENG / Видео'.",
          telegramText: "Текст, который будет возвращён после создания промокода. Например, если у вас есть промокод 'promo123', и вы создаёте текст вида 'Linebet is the best bookmaker. Our promo - promo123', то ваш окончательный текст должен выглядеть так: 'Linebet is the best bookmaker. Our promo - {promocode}'. После полного создания промокода этот текст будет возвращён вам в Telegram-боте.",
        },
        telegram: {
          userId: 'Укажите числовой ID чата пользователя в Telegram (можно получить через @userinfobot или у пользователя).',
        },
        promocodeTemplates: {
          title: 'Шаблоны для ваших промокодов',
          name: 'Название шаблона',
          file: 'Выберите фото или видео',
          category: "Выберите категорию к которой принадлежит шаблон. Также, категории являются кнопками в боте",
          template: 'Здесь вы можете редактировать поле промокода с помощью мыши. Просто выделите область, удерживая левую кнопку мыши.',
          test: 'Здесь вы можете протестировать свой шаблон с пользовательским словом. Сначала сохраните свой шаблон. После этого вы можете протестировать свой промокод, используя кнопку ниже.',
        },
      },
    };
  }

  // Английские переводы (по умолчанию)
  return {
    promocodeGenerator: 'Promocode generator',
    adminPanel: 'Admin panel',
    categories: 'Categories',
    templates: 'Templates',
    users: 'Users',
    user: { notFound: 'No users' },
    access: { hasAccess: 'Has access', noAccess: 'No access' },
    home: 'Home',
    telegramAccess: 'Telegram access',
    giveTelegramAccess: 'Give access to bot',
    logout: 'Logout',
    theme: { light: 'Light theme', dark: 'Dark theme' },
    chooseBot: 'Choose your bot',
    createdBy: 'Created by',
    permissions: 'Permissions',
    category: {
      create: 'Create category (country/language)',
      enter: 'Enter category',
      disable: 'Disable',
      enable: 'Enable',
      isDisabled: 'Category is disabled',
      isEnabled: 'Category is enabled',
      promocodeTemplates: 'Show promocode templates',
      notFound: 'No categories',
    },
    promocode: {
      create: 'Create promocode template',
      edite: 'Edite promocode template',
      image: 'image',
      video: 'video',
      waitingForSetup: 'Waiting for setup',
      notFound: 'No templates',
      restartVideo: 'Restart video',
      restartGif: 'Restart GIF',
      mute: 'Mute',
      unmute: 'Unmute',
    },
    manage: {
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      saving: 'Saving…',
      attach: 'Attach',
      search: 'Search',
      create: 'Create',
      back: 'Back',
    },
    paginationOf: 'of',
    errors: {
      loadTemplatesFailed: 'Failed to load templates',
      deleteTemplate: 'Error deleting template',
      createTemplate: 'Error creating template',
      updateTemplate: 'Error updating template',
      deleteTelegramUser: 'Error deleting telegram user',
      toggleAccess: 'Error toggling access',
      deleteUser: 'Error deleting user',
      createTelegramUser: 'Error creating telegram user',
      updateTelegramUser: 'Error updating telegram user',
      invalidPassword: 'Invalid password',
      createUser: 'Error creating user',
      updateUser: 'Error updating user',
      deleteCategory: 'Error deleting category',
      createCategory: 'Error creating category',
      updateCategory: 'Error updating category',
      toggleCategoryVisibility: 'Error toggling category visibility',
      loginFailed: 'Login failed',
      loadCategoriesFailed: 'Failed to load categories',
      passwordsDoNotMatch: 'Passwords do not match',
      registrationFailed: 'Registration failed',
    },
    validation: {
      password: {
        minLength: 'Minimum 8 characters',
        uppercase: 'At least one uppercase letter required',
        digit: 'At least one digit required',
        special: 'At least one special character (!@#$%&*+-=?) required',
      },
      selectFile: 'Please select a file',
      selectCategory: 'Please select at least one category',
      selectArea: 'Select the promocode area on the image',
      selectFont: 'Please select a font',
    },
    formField: {
      name: 'Name',
      image: 'Image',
      file: 'File',
      files: 'files',
      categories: 'Categories',
      parent: 'Parent category',
      parameters: 'Parameters',
      promoTest: 'Promo Test',
      testBtn: 'Test',
      imageParams: 'Image parameters',
      textColor: 'Text color',
      textFontSize: 'Text Font Size',
      textFont: 'Text font',
      substrateSettings: 'Substrate settings',
      render: 'Render',
      bgColor: 'Background color',
      upperRadius: 'Upper radius',
      sidesRadius: 'Sides radius',
      borderSize: 'Border size',
      borderColor: 'Border color',
      shadowSettings: 'Shadow settings',
      shadowBold: 'Shadow bold',
      shadowUpperSize: 'Shadow upper size',
      shadowSidesSize: 'Shadow sides size',
      headerSettings: 'Header Settings',
      renderHeader: 'Render header',
      headerColor: 'Header color',
      headerText: 'Header text',
      headerTextFont: 'Header text font',
      headerTextFontSize: 'Header text font size',
      telegramText: 'Telegram text',
      password: 'Password',
      passwordLeaveEmpty: 'Leave empty to keep unchanged',
      generate: 'Generate',
      generatePassword: 'Generate',
      passwordRules: 'Min 8 characters, required: special char, digit, uppercase letter',
      permissions: 'Permissions',
      namePlaceholder: 'Input name here',
      passwordPlaceholder: 'Input password here',
      chooseUser: 'Choose user',
      telegramBot: 'Telegram Bot',
      telegramUserName: 'Telegram user name',
      telegramUserId: 'Telegram user ID',
      selectBot: 'Select a bot',
      telegramUserPlaceholder: 'Enter telegram user name',
      telegramUserIdPlaceholder: 'Enter telegram user ID',
      chooseParent: 'Choose parent category',
      roleId: 'Role ID',
      promocodeTemplates: 'Promocode templates',
      angle: 'Angle',
      startTime: 'Start',
      endTime: 'End',
      createUser: 'Create new user',
      giveAdminAccess: 'Give access to telegram user',
      yourCurrentTemplate: 'Your current template',
      dragndrop:'Drag and drop a file or click to select'
    },
    attachUsers: 'Attach permissions to user',
    selectUser: 'Select user',
    chooseUser: 'Choose user',
    userName: 'Name',
    userEmail: 'Email',
    userPermissions: 'Permissions',
    createdAt: 'Created at',
    categoryName: 'Category',
    categoryCreateBtn: 'Create category',
    Login: 'Login',
    'Logging in...': 'Logging in...',
    'Loading...': 'Loading...',
    helpers: {
      category: {
        title: "Also it's a buttons in a telegram bot",
        name: 'Name of category',
        promocodeTemplate: 'You can choose another promocode template or templates for category',
        parentCategory: "If you want to create a subcategory, you need to select a main category. For example, if you want to create a new category called 'Videos' and choose 'ENG' as the parent category, after saving, you will get 'ENG / Videos'",
        telegramText: "The text that will be returned after creating a promocode. For example, if you have a promocode 'promo123' and you create a text like 'Linebet is the best bookmaker. Our promo - promo123', your final text should look like: 'Linebet is the best bookmaker. Our promo - {promocode}'. When you have completely created the promocode, this text will be returned to you in the Telegram bot.",
      },
      telegram: {
        userId: 'Enter the numeric Telegram chat ID (e.g. from @userinfobot or from the user).',
      },
      promocodeTemplates: {
        title: 'Templates for your promocodes',
        name: 'Template name',
        file: 'Choose a photo or video',
        category: "Choose a category for promocode template. It's also buttons on telegram bot",
        template: 'Here you can edit the promocode field using the mouse. Simply select the area by holding down the left mouse button.',
        test: 'Here you can test your template with a custom word. First, you should save your template. After that, you can test your promo using the button below.',
      },
    },
  };
};

// Функция для получения перевода по ключу (поддерживает вложенные ключи через точку)
export const t = (key: string, locale?: Locale): string => {
  const loc = locale || currentLocale;
  const translations = translationsCache[loc] || getLocalTranslations(loc);
  
  const keys = key.split('.');
  let value: any = translations;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // Если ключ не найден, возвращаем сам ключ
      return key;
    }
  }
  
  return typeof value === 'string' ? value : key;
};

// Установка локали
export const setLocale = async (locale: Locale): Promise<void> => {
  currentLocale = locale;
  await loadTranslations(locale);
  localStorage.setItem('locale', locale);
  // Заглушка: запрос к API отключён
};

// Получение текущей локали
export const getLocale = (): Locale => {
  return currentLocale;
};

// Инициализация переводов
export const initTranslations = async (locale?: Locale): Promise<void> => {
  const savedLocale = (localStorage.getItem('locale') as Locale) || locale || getBrowserLocale();
  currentLocale = savedLocale;
  await loadTranslations(currentLocale);
};

// Экспорт для использования в компонентах
export const translationService = {
  t,
  setLocale,
  getLocale,
  initTranslations,
};
